import { useEffect, useState } from "react";
import { apiConfig, tryRefresh } from "@/services/api";
import { useAuthStore } from "@/store/authStore";

/**
 * 私有凭证图片渲染（SEC-2）· 私有上传路由 `/api/v1/uploads/private/{key}` 要求 Bearer token，
 * 普通 `<img src>` 无法带自定义请求头——本组件改用 fetch 带 token 取字节，转 blob URL 再渲染，
 * 卸载/URL 变化时 revoke 释放内存。401 时走与 uploadReceipt 同款 token 刷新重试一次。
 *
 * 仅用于报销凭证/车辆证照等敏感件的展示点；产品图/Logo 等公开件维持普通 `<img src={apiImg(...)}>`，
 * 不要在公开件上套本组件（多一次网络往返，且公开件本就不需要鉴权）。
 */
export function AuthedImage({
  src,
  alt,
  className,
  onClick,
}: {
  src: string;
  alt?: string;
  className?: string;
  onClick?: (e: React.MouseEvent<HTMLImageElement>) => void;
}) {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!src) { setBlobUrl(null); setFailed(false); return; }
    let cancelled = false;
    let objectUrl: string | null = null;

    const full = src.startsWith("http") ? src : `${apiConfig.base}${src}`;
    const doFetch = () => {
      const token = useAuthStore.getState().accessToken;
      const tenantSlug = useAuthStore.getState().user?.tenantSlug;
      return fetch(full, {
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          ...(tenantSlug ? { "X-Tenant-Slug": tenantSlug } : {}),
        },
      });
    };

    setFailed(false);
    (async () => {
      let res = await doFetch();
      if (res.status === 401 && useAuthStore.getState().refreshToken) {
        const ok = await tryRefresh();
        if (ok) res = await doFetch();
      }
      if (!res.ok) throw new Error(`加载失败 ${res.status}`);
      const blob = await res.blob();
      if (cancelled) return;
      objectUrl = URL.createObjectURL(blob);
      setBlobUrl(objectUrl);
    })().catch(() => { if (!cancelled) setFailed(true); });

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [src]);

  if (failed) {
    return <div className={className} title="加载失败" style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>⚠️</div>;
  }
  if (!blobUrl) {
    return <div className={className} style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>⏳</div>;
  }
  return <img src={blobUrl} alt={alt} className={className} onClick={onClick} />;
}
